import { Alert, Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ONBOARDING_STEPS, connectorRequestSchema } from '@app/shared';
import type {
  ConnectorRequestDto,
  ConnectorRequestInput,
  OnboardingDto,
  ReadinessItemDto,
  SampleRemovedDto,
  SampleSetupDto,
  SetupImportDto,
} from '@app/shared';
import { APP_NAME } from '../../app/theme';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { ApiError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';

/**
 * Settings > Get started (PC239, administrators): a new MVNO's set-up, step by step, from the
 * go-live checklist; "Request Live" when its part is done; "My carrier is not listed".
 */

const KEY = ['onboarding'];
const MARK = { ok: '✔', todo: '✖', info: 'ℹ' } as const;
const MARK_COLOR = { ok: 'success.main', todo: 'error.main', info: 'text.secondary' } as const;
const REQUEST_COLOR = {
  Open: 'warning',
  'In progress': 'info',
  Done: 'success',
  Declined: 'default',
} as const;

function Item({ i }: { i: ReadinessItemDto }) {
  return (
    <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Typography component="span" color={MARK_COLOR[i.status]} aria-label={i.status}>
        {MARK[i.status]}
      </Typography>
      <Typography component="span" variant="body2" sx={{ flex: 1, minWidth: 220 }}>
        <b>{i.label}</b>: {i.detail}
      </Typography>
      {i.link && i.status !== 'ok' ? (
        <Button component={Link} to={i.link} size="small">
          Open
        </Button>
      ) : null}
    </Box>
  );
}

const blankRequest: ConnectorRequestInput = {
  carrierName: '',
  docsUrl: '',
  contactName: '',
  contactEmail: '',
  notes: '',
};

function ConnectorRequestDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [v, setV] = useState<ConnectorRequestInput>(blankRequest);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const send = useMutation({
    mutationFn: (body: ConnectorRequestInput) =>
      api.post<ConnectorRequestDto>('/onboarding/connector-requests', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
  const field = (name: keyof ConnectorRequestInput) => ({
    value: v[name] ?? '',
    onChange: (e: { target: { value: string } }) => setV((x) => ({ ...x, [name]: e.target.value })),
    error: !!problems[name],
    helperText: problems[name],
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const checked = connectorRequestSchema.safeParse(v);
    if (!checked.success)
      return setProblems(
        Object.fromEntries(checked.error.issues.map((i) => [String(i.path[0]), i.message])),
      );
    try {
      await send.mutateAsync(v);
      notify.success(`Request sent. ${APP_NAME} answers it here.`);
      onClose();
    } catch (err) {
      const found =
        err instanceof ApiError
          ? Object.fromEntries(err.details.map((d) => [d.path, d.message]))
          : {};
      setProblems(found);
      if (!Object.keys(found).length) notify.error(err);
    }
  };
  return (
    <FormDialog
      open
      title="My carrier is not listed"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={send.isPending}
      submitLabel="Send request"
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {APP_NAME} sets up a connector for your carrier or MVNE from its API documents, once, and
          offers it to you. You then pick it on Settings &gt; Connections and type your own values.
          You can also make one yourself under Carrier connectors there.
        </Typography>
        <TextField required label="Carrier or MVNE" {...field('carrierName')} />
        <TextField label="Link to its API documents" placeholder="https://" {...field('docsUrl')} />
        <TextField label="Contact at the carrier" {...field('contactName')} />
        <TextField label="Contact e-mail" {...field('contactEmail')} />
        <TextField
          multiline
          minRows={3}
          label="Notes"
          placeholder="Which services you use (activations, ports, eSIM ...), sandbox access, when you need it"
          {...field('notes')}
        />
      </Box>
    </FormDialog>
  );
}

/** "Import a set-up file": the file is checked first; nothing changes until Import is pressed. */
/** The checklist has a line per carrier's plans once a company has a carrier. */
const hasPlans = (i: { key: string }) => i.key.startsWith('plans-');

/** A company in Trial with no plans can load an invented set-up and sell a line straight away. */
function SampleSetupCard({
  done,
  onDone,
}: {
  done: SampleSetupDto | null;
  onDone: (result: SampleSetupDto) => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const load = useMutation({
    mutationFn: () => api.post<SampleSetupDto>('/onboarding/sample-setup'),
    onSuccess: (result) => {
      onDone(result);
      notify.success('Sample set-up loaded.');
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => notify.error(err),
  });
  return (
    <Paper variant="outlined" component="section" aria-label="Try it now" sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Try it now
      </Typography>
      {done ? (
        <Typography variant="body2">
          Loaded. Start a new order with ZIP code {done.zips.join(', ')} and use SIM {done.sims[0]}.
          The carrier and everything else are simulated, so no real line is made.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Load a sample carrier, plans, ZIP codes and SIMs, all invented, and sell your first line
            in a few minutes, before you connect any account.
          </Typography>
          <Button variant="contained" disabled={load.isPending} onClick={() => load.mutate()}>
            {load.isPending ? 'Loading…' : 'Load a sample set-up'}
          </Button>
        </>
      )}
    </Paper>
  );
}

/** The sample stays until it is removed: its carrier can never be connected, so it blocks Live. */
function RemoveSampleCard({ onDone }: { onDone: (result: SampleRemovedDto) => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.post<SampleRemovedDto>('/onboarding/sample-setup/remove'),
    onSuccess: (result) => {
      setAsking(false);
      onDone(result);
      notify.success('Sample set-up removed.');
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => {
      setAsking(false);
      notify.error(err);
    },
  });
  return (
    <Paper variant="outlined" component="section" aria-label="Sample set-up" sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Sample set-up
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The invented carrier, plans, SIMs and ZIP codes are still in your company. Remove them
        before you go Live: the sample plans can be sold, and the sample carrier can never be
        connected.
      </Typography>
      <Button variant="outlined" color="error" onClick={() => setAsking(true)}>
        Remove the sample set-up
      </Button>
      <ConfirmDialog
        open={asking}
        title="Remove the sample set-up"
        message="This takes out the sample carrier, its plans and top-up, the sample SIM model, the unsold sample SIMs and the six sample ZIP codes. Your own carriers, plans and SIMs stay. A test sale stays as history."
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setAsking(false)}
      />
    </Paper>
  );
}

function SetupImportCard() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<SetupImportDto | null>(null);
  const send = useMutation({
    mutationFn: ({ chosen, dryRun }: { chosen: File; dryRun: boolean }) => {
      const form = new FormData();
      form.append('file', chosen);
      return api.upload<SetupImportDto>(`/onboarding/setup-import?dryRun=${dryRun}`, form);
    },
  });
  const choose = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = '';
    if (!chosen) return;
    setFile(chosen);
    send.mutate(
      { chosen, dryRun: true },
      { onSuccess: setPreview, onError: (err) => notify.error(err) },
    );
  };
  const load = () => {
    if (!file) return;
    send.mutate(
      { chosen: file, dryRun: false },
      {
        onSuccess: (done) => {
          const added = done.sections.reduce((sum, x) => sum + x.add, 0);
          notify.success(`Set-up imported: ${added} record(s) added.`);
          setPreview(null);
          void queryClient.invalidateQueries({ queryKey: KEY });
        },
        onError: (err) => notify.error(err),
      },
    );
  };
  return (
    <Paper variant="outlined" component="section" aria-label="Import a set-up file" sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Import a set-up file
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Moving from another system? A set-up file loads your carriers, wholesale codes, plans,
        top-ups, ZIP codes, product models, message templates and settings in one go. It only adds
        what you do not have yet; credentials are never in it.
      </Typography>
      <Button variant="outlined" disabled={send.isPending} onClick={() => input.current?.click()}>
        {send.isPending ? 'Working…' : 'Choose a file'}
      </Button>
      <input
        ref={input}
        type="file"
        accept="application/json,.json"
        hidden
        aria-label="Set-up file"
        onChange={choose}
      />
      {preview ? (
        <Box aria-label="What the file adds" sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
          {preview.source ? <Typography variant="body2">From: {preview.source}</Typography> : null}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {preview.sections.map((x) => (
              <Typography component="li" variant="body2" key={x.name}>
                {x.name}: {x.add} to add, {x.keep} already here
              </Typography>
            ))}
          </Box>
          {preview.problems.map((p) => (
            <Alert severity="warning" key={p}>
              {p}
            </Alert>
          ))}
          <Box>
            <Button variant="contained" disabled={send.isPending} onClick={load}>
              Import
            </Button>
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
}

function LiveRequestDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const send = useMutation({
    mutationFn: () => api.post<OnboardingDto>('/onboarding/live-request', { note }),
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
  });
  return (
    <FormDialog
      open
      title="Request Live"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate(undefined, {
          onSuccess: () => {
            notify.success(`Request sent. ${APP_NAME} moves your company to Live.`);
            onClose();
          },
          onError: (err) => notify.error(err),
        });
      }}
      submitting={send.isPending}
      submitLabel="Request Live"
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2">
          Once Live, a service you have not connected is refused instead of simulated. Real lines
          and real money from then on.
        </Typography>
        <TextField
          multiline
          minRows={2}
          label="Note (optional)"
          placeholder="For example the day you want to start"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
      </Box>
    </FormDialog>
  );
}

export function GetStartedPage() {
  const { user } = useAuth();
  const featureOn = useFeatureReleased();
  const [asking, setAsking] = useState(false);
  // Kept here: loading the sample adds plans, which would otherwise take its card, and its answer, away.
  const [sample, setSample] = useState<SampleSetupDto | null>(null);
  const [removed, setRemoved] = useState<SampleRemovedDto | null>(null);
  const [live, setLive] = useState(false);
  const data = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<OnboardingDto>('/onboarding'),
    enabled: user?.level === 'admin',
  });
  if (user?.level !== 'admin')
    return (
      <Box>
        <PageHeader title="Get started" code="PC239" />
        <Alert severity="info">Only an administrator sets up the company.</Alert>
      </Box>
    );
  const d = data.data;
  const companyLeft = (d?.items ?? []).filter(
    (i) => i.status === 'todo' && i.step !== 'platform' && i.key !== 'stage',
  );
  return (
    <Box>
      <PageHeader title="Get started" code="PC239" />
      {data.isError ? (
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      ) : !d ? (
        <LoadingState />
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Paper variant="outlined" component="section" aria-label="Your company" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h3">{d.company.name}</Typography>
              <Chip
                size="small"
                color={d.company.stage === 'Live' ? 'success' : 'warning'}
                label={d.company.stage}
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {d.company.stage === 'Live'
                ? 'Your company is Live: real lines and real money.'
                : `Your company is in Trial: what you have not connected yet is simulated, so you can try every screen. Work through the steps below at your own pace; ${companyLeft.length} item(s) are left before you can ask to go Live.`}
            </Typography>
          </Paper>

          {sample ||
          (d.company.stage === 'Trial' && featureOn('sample-setup') && !d.items.some(hasPlans)) ? (
            <SampleSetupCard done={sample} onDone={setSample} />
          ) : null}
          {d.sample && featureOn('sample-setup-remove') ? (
            <RemoveSampleCard
              onDone={(result) => {
                setSample(null);
                setRemoved(result);
              }}
            />
          ) : null}
          {removed ? (
            <Alert severity="success" onClose={() => setRemoved(null)}>
              Removed {removed.plans} plan(s), {removed.topups} top-up(s), {removed.sims} SIM(s) and{' '}
              {removed.zips} ZIP code(s).
              {removed.kept
                ? ' Test sales stay as history, so the sample is retired there.'
                : ''}{' '}
              If your own set-up file lists these ZIP codes, import it again: it only adds what is
              missing.
            </Alert>
          ) : null}
          {d.company.stage === 'Trial' && featureOn('setup-import') ? <SetupImportCard /> : null}

          {ONBOARDING_STEPS.map((step, n) => {
            const items = d.items.filter((i) => i.step === step.key);
            const left = items.filter((i) => i.status === 'todo').length;
            return (
              <Paper
                key={step.key}
                variant="outlined"
                component="section"
                aria-label={step.title}
                sx={{ p: 2 }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Typography variant="h3">
                    {n + 1}. {step.title}
                  </Typography>
                  {items.length ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={left ? 'error' : 'success'}
                      label={left ? `${left} to do` : 'Done'}
                    />
                  ) : null}
                </Box>
                {step.key === 'platform' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {APP_NAME} looks after these on the server; nothing for you to do.
                  </Typography>
                ) : null}
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 0.75 }}
                >
                  {items.map((i) => (
                    <Item key={i.key} i={i} />
                  ))}
                </Box>
                {step.key === 'carriers' ? (
                  <Box sx={{ mt: 1.5 }}>
                    <Button variant="outlined" size="small" onClick={() => setAsking(true)}>
                      My carrier is not listed
                    </Button>
                    {d.requests.length ? (
                      <Box
                        component="ul"
                        aria-label="Your connector requests"
                        sx={{ m: 0, mt: 1, pl: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}
                      >
                        {d.requests.map((r) => (
                          <Box component="li" key={r.id} sx={{ display: 'flex', gap: 1 }}>
                            <Chip size="small" color={REQUEST_COLOR[r.status]} label={r.status} />
                            <Typography variant="body2">
                              <b>{r.carrierName}</b>, asked {formatDateTime(r.createdAt)}
                              {r.reply ? `: ${r.reply}` : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
                {step.key === 'live' && d.company.stage === 'Trial' ? (
                  <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
                    {d.liveRequest ? (
                      <Alert severity="info">
                        Live requested {formatDateTime(d.liveRequest.at)} by {d.liveRequest.by}
                        {d.liveRequest.note ? ` (${d.liveRequest.note})` : ''}. {APP_NAME} moves
                        your company to Live.
                      </Alert>
                    ) : null}
                    <Box>
                      <Button
                        variant="contained"
                        disabled={companyLeft.length > 0}
                        onClick={() => setLive(true)}
                      >
                        {d.liveRequest ? 'Request Live again' : 'Request Live'}
                      </Button>
                      {companyLeft.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Finish first: {companyLeft.map((i) => i.label).join(', ')}.
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : null}
              </Paper>
            );
          })}
        </Box>
      )}
      {asking ? <ConnectorRequestDialog onClose={() => setAsking(false)} /> : null}
      {live ? <LiveRequestDialog onClose={() => setLive(false)} /> : null}
    </Box>
  );
}
